/**
 * Runtime configuration, from `/config.json` on this origin.
 *
 * **Nothing about the environment is compiled into this app.** It was, twice — first as
 * `VITE_*` build-time variables, then partly as an API call — and the build-time version
 * produced the failure it was always going to: changing an environment needed a rebuild,
 * the value was baked into a content-hashed asset, and a stale bundle was
 * indistinguishable from a wrong value. Dotenv also puts a file that looks like the place
 * secrets go next to code that gets committed.
 *
 * One mechanism now, and the same code path in both places: deployed, `EdgeStack` writes
 * this file into the web bucket beside the app; locally, `npm run web:config` writes the
 * same file into `apps/web/public`. The fetch is relative either way.
 *
 * `apiUrl` and `mediaUrl` are **optional**, because deployed there is nothing to say —
 * the app shares an origin with `/v1/*` and `/media/*`. Only a local developer needs to
 * name a backend, since there is no local one (D30).
 */

import { type RuntimeConfigFile, requireSignInFields, usableStripeKey } from '@going-once/contracts/runtime-config';

export interface RuntimeConfig {
  /** The user pool this app signs in to, with SRP. */
  userPoolId: string;
  /** The public app client id for browser sign-in. */
  webClientId: string;
  /**
   * Staff sign in with Google through their own client, and only its tokens carry a staff
   * role (GO-568). Both absent where Google sign-in is not configured; the button then is
   * not shown.
   */
  staffSignIn?: { staffClientId: string; googleClientId: string };
  /** Where to send capability calls. Same origin when absent. */
  apiUrl: string;
  /** Where listing photos are served from. `${origin}/media` when absent. */
  mediaUrl: string;
  /**
   * The AppSync Events realtime host and its subscribe key (D50).
   *
   * **Optional, and the app works without them** — a deployment with no realtime stack, or
   * a local run before `npm run web:config` has been re-run. The live screen falls back to
   * the polling it already does, which is the same reason the subscription is never the
   * source of truth.
   *
   * Not a secret: the key can subscribe to channels a guest could already read through
   * public capabilities, and it cannot publish. Everything a person does goes through a
   * capability instead.
   */
  realtime?: { url: string; httpUrl: string; apiKey: string };
  /**
   * Stripe's publishable key, for mounting a Payment Element.
   *
   * **Optional, and the placeholder counts as absent.** `ApiStack` creates the parameter
   * as `pk_unset` and a person sets the real value, so an environment where nobody has
   * yet is a legitimate one — and the card screen saying "not configured here" is far
   * better than Stripe.js loading against a key that fails on submit, which surfaces as
   * an error inside an iframe at the worst possible moment.
   *
   * Not a secret: it ships in every page that mounts an Element, exactly as the realtime
   * subscribe key does. The secret key it pairs with never leaves Secrets Manager.
   */
  stripePublishableKey?: string;
  /** The environment's name, from `config.json`. Absent from an edge deployed before 2026-10-03. */
  namespace?: string;
}

let loaded: RuntimeConfig | undefined;

export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  if (loaded) return loaded;

  // Cache-busted: this file names the environment, and a browser holding yesterday's
  // copy is the exact failure this whole mechanism exists to prevent.
  const response = await fetch(`/config.json?t=${Date.now()}`, { cache: 'no-store' });
  if (!response.ok) {
    throw new Error(
      `/config.json returned ${response.status}. Deployed, EdgeStack writes it; locally, run \`npm run web:config\`.`,
    );
  }

  const file = (await response.json()) as RuntimeConfigFile;
  const { userPoolId, clientId } = requireSignInFields(
    file,
    'webClientId',
    'Deployed, EdgeStack writes it; locally, run `npm run web:config`.',
  );

  const origin = window.location.origin;
  loaded = {
    userPoolId,
    webClientId: clientId,
    ...(file.staffClientId && file.googleClientId
      ? { staffSignIn: { staffClientId: file.staffClientId, googleClientId: file.googleClientId } }
      : {}),
    apiUrl: (file.apiUrl ?? origin).replace(/\/$/, ''),
    mediaUrl: (file.mediaUrl ?? `${origin}/media`).replace(/\/$/, ''),
    ...(file.realtimeUrl && file.realtimeHttpUrl && file.realtimeApiKey
      ? { realtime: { url: file.realtimeUrl, httpUrl: file.realtimeHttpUrl, apiKey: file.realtimeApiKey } }
      : {}),
    // `pk_unset` is the placeholder CDK writes, and it is treated as absent rather than
    // passed to Stripe.js — an unconfigured environment should say so, not fail on submit.
    ...(usableStripeKey(file.stripePublishableKey)
      ? { stripePublishableKey: usableStripeKey(file.stripePublishableKey) as string }
      : {}),
    ...(file.namespace ? { namespace: file.namespace } : {}),
  };
  return loaded;
}

/** The config, once loaded. Throws rather than guessing — guessing sends a browser somewhere wrong. */
export function runtimeConfig(): RuntimeConfig {
  if (!loaded) throw new Error('Runtime config not loaded yet — call loadRuntimeConfig() first.');
  return loaded;
}

/**
 * **Whether this environment shows team tools** — the CSV import harness (D55; O32). Never
 * in production, where the web is not a seller surface; and never where the environment
 * does not name itself, so an edge deployed before `config.json` carried `namespace` fails
 * closed. The link to a tool *and* the tool's own route ask this (PR #44 review).
 */
export function teamToolsEnabled(): boolean {
  const { namespace } = runtimeConfig();
  return namespace !== undefined && namespace !== 'prod';
}
